import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Building2,
  Loader2,
  Layers,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Users,
  Eye,
  Clock,
  MapPin,
  Send,
  Trash2,
  FileEdit,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  isoMonday,
  isoString,
  fmtHours,
} from "@/pages/schedule/helpers";
import ScheduleGrid from "@/pages/schedule/ScheduleGrid";
import MonthGrid from "@/pages/schedule/MonthGrid";
import DayList from "@/pages/schedule/DayList";
import ShiftEditDialog from "@/pages/schedule/ShiftEditDialog";
import TemplatesManagerDialog from "@/pages/schedule/TemplatesManagerDialog";
import DraftsManagerDialog from "@/pages/schedule/DraftsManagerDialog";
import ConfirmDialog from "@/pages/schedule/ConfirmDialog";

const VIEW_OVERVIEW = "__all__"; // "Összes telephely" pseudo-id
const PERIOD_WEEK = "week";
const PERIOD_MONTH = "month";
const PERIOD_DAY = "day";

export default function SchedulePage() {
  // Mobilon "napi" alapértelmezett — a heti grid nem fér ki kis képernyőre.
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const [period, setPeriod] = useState(isMobile ? PERIOD_DAY : PERIOD_WEEK);
  // anchor date — heti nézet: hét hétfője; havi: hónap 1-je; napi: konkrét nap
  const [anchorDate, setAnchorDate] = useState(() => isoString(isoMonday()));
  const [activeSiteId, setActiveSiteId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [templatesManagerOpen, setTemplatesManagerOpen] = useState(false);
  const [draftsManagerOpen, setDraftsManagerOpen] = useState(false);
  const [draftsPendingCount, setDraftsPendingCount] = useState(0);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftEditing, setShiftEditing] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState({ open: false, ids: [] });
  const [publishing, setPublishing] = useState(false);

  // Stable callback: shift-block kattintásra megnyitja a szerkesztő modalt.
  // useCallback nélkül minden parent re-render új function-t adna át a gridnek
  // → minden shift block re-renderelne, lagosan.
  const openShiftEditor = useCallback((sh) => {
    setShiftEditing(sh);
    setShiftDialogOpen(true);
  }, []);

  // Compute date range based on period + anchor
  const { fromDate, toDate } = useMemo(() => {
    const a = new Date(anchorDate + "T00:00:00");
    if (period === PERIOD_WEEK) {
      const mon = new Date(a);
      const dow = (mon.getDay() + 6) % 7;
      mon.setDate(mon.getDate() - dow);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { fromDate: isoString(mon), toDate: isoString(sun) };
    }
    if (period === PERIOD_MONTH) {
      const first = new Date(a.getFullYear(), a.getMonth(), 1);
      const last = new Date(a.getFullYear(), a.getMonth() + 1, 0);
      return { fromDate: isoString(first), toDate: isoString(last) };
    }
    // DAY
    return { fromDate: anchorDate, toDate: anchorDate };
  }, [period, anchorDate]);

  // Adatok mindig a teljes tartományra — minden telephelyre vagy a kiválasztottra
  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      // Optimalizáció: ha konkrét telephely van kiválasztva, csak azt töltsük
      // be (a backend egy-elemű sites tömbbel válaszol). Picker / overview
      // nézetben minden telephely kell.
      if (activeSiteId && activeSiteId !== VIEW_OVERVIEW) {
        params.set("site_id", activeSiteId);
      }
      const { data } = await api.get(`/shifts/range?${params.toString()}`);
      setScheduleData(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Adatok betöltése sikertelen.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, activeSiteId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  // Reset selection when range changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [fromDate, toDate, activeSiteId, period]);

  const loadTemplates = useCallback(async () => {
    if (!activeSiteId || activeSiteId === VIEW_OVERVIEW) {
      setTemplates([]);
      return;
    }
    try {
      const { data } = await api.get(`/shift-templates?site_id=${activeSiteId}`);
      setTemplates(data || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Sablonok betöltése sikertelen.");
    }
  }, [activeSiteId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load pending drafts count for the current range / site
  const loadDraftsCount = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status: "pending", from: fromDate, to: toDate });
      if (activeSiteId && activeSiteId !== VIEW_OVERVIEW) {
        params.set("site_id", activeSiteId);
      }
      const { data } = await api.get(`/shift-drafts?${params.toString()}`);
      setDraftsPendingCount(Array.isArray(data) ? data.length : 0);
    } catch (e) {
      setDraftsPendingCount(0);
    }
  }, [fromDate, toDate, activeSiteId]);

  useEffect(() => {
    loadDraftsCount();
  }, [loadDraftsCount]);

  // Ha a sablonok-kezelése overlay BEZÁRUL (true -> false), frissítsük a
  // templates listát. Ref-fel kerüljük a duplikált mount-time fetch-et.
  const prevTplOpenRef = useRef(false);
  useEffect(() => {
    if (prevTplOpenRef.current && !templatesManagerOpen) {
      loadTemplates();
    }
    prevTplOpenRef.current = templatesManagerOpen;
  }, [templatesManagerOpen, loadTemplates]);

  const onAssignTemplate = useCallback(
    async (templateId, employeeId, date) => {
      const tpl = templates.find((t) => t._id === templateId);
      if (!tpl) {
        toast.error("Ismeretlen sablon.");
        return;
      }
      try {
        await api.post("/shifts", {
          site_id: tpl.site_id,
          employee_id: employeeId,
          date,
          template_id: templateId,
        });
        await loadSchedule();
      } catch (e) {
        toast.error(formatApiError(e) || "Beosztás létrehozása sikertelen.");
      }
    },
    [templates, loadSchedule],
  );

  const onToggleSelect = useCallback((shiftId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId);
      else next.add(shiftId);
      return next;
    });
  }, []);

  const onBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/shifts/bulk-delete", {
        ids: Array.from(selectedIds),
      });
      toast.success(`${data.deleted} műszak törölve.`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      await loadSchedule();
    } catch (e) {
      toast.error(formatApiError(e) || "Tömeges törlés sikertelen.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---- Draft approve/reject (inline from grid) ----
  const [approvingDraftId, setApprovingDraftId] = useState(null);

  const onApproveDraft = useCallback(async (draft) => {
    const id = draft.id || draft._id;
    if (!id) return;
    setApprovingDraftId(id);
    try {
      await api.post(`/shift-drafts/${id}/approve`);
      toast.success(
        draft.is_day_off
          ? `Szabadnap jóváhagyva: ${draft.employee_name} · ${draft.date}`
          : `Tervezet jóváhagyva: ${draft.employee_name} · ${draft.date}`,
      );
      await loadSchedule();
      await loadDraftsCount();
    } catch (e) {
      toast.error(formatApiError(e) || "Jóváhagyás sikertelen.");
    } finally {
      setApprovingDraftId(null);
    }
  }, [loadSchedule, loadDraftsCount]);

  const onRejectDraft = useCallback(async (draft) => {
    const id = draft.id || draft._id;
    if (!id) return;
    setApprovingDraftId(id);
    try {
      await api.post(`/shift-drafts/${id}/reject`);
      toast.success("Tervezet elutasítva.");
      await loadSchedule();
      await loadDraftsCount();
    } catch (e) {
      toast.error(formatApiError(e) || "Elutasítás sikertelen.");
    } finally {
      setApprovingDraftId(null);
    }
  }, [loadSchedule, loadDraftsCount]);

  const sections = scheduleData?.sites || [];
  const activeSection = useMemo(
    () => sections.find((s) => s.site_id === activeSiteId),
    [sections, activeSiteId],
  );

  const onPublish = () => {
    if (!activeSiteId || activeSiteId === VIEW_OVERVIEW) return;
    const useSelection = selectedIds.size > 0;

    // Filter: csak nem publikált shifteket küldjünk
    const sectionShifts = (activeSection?.employees || []).flatMap(
      (e) => e.shifts || [],
    );
    let targetIds = [];
    if (useSelection) {
      targetIds = Array.from(selectedIds).filter((id) => {
        const sh = sectionShifts.find((s) => s._id === id);
        return sh && !sh.published;
      });
    } else {
      targetIds = sectionShifts.filter((s) => !s.published).map((s) => s._id);
    }

    if (targetIds.length === 0) {
      toast.info("Nincs publikálandó műszak — minden már publikálva van.");
      return;
    }
    setPublishConfirm({ open: true, ids: targetIds });
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/shifts/publish", {
        ids: publishConfirm.ids,
      });
      toast.success(
        `${data.published} műszak publikálva · ${data.notified_employees} dolgozó értesítve`,
      );
      setSelectedIds(new Set());
      setPublishConfirm({ open: false, ids: [] });
      await loadSchedule();
    } catch (e) {
      toast.error(formatApiError(e) || "Publikálás sikertelen.");
    } finally {
      setPublishing(false);
    }
  };

  // ---- Publish status of the current visible section ----
  const publishStatus = useMemo(() => {
    if (!activeSection || activeSiteId === VIEW_OVERVIEW || activeSiteId === null) {
      return { total: 0, unpublished: 0, allPublished: true };
    }
    const all = (activeSection.employees || []).flatMap((e) => e.shifts || []);
    const total = all.length;
    const unpublished = all.filter((s) => !s.published).length;
    return { total, unpublished, allPublished: total > 0 && unpublished === 0 };
  }, [activeSection, activeSiteId]);

  // ---- Navigation ----
  const shiftPeriod = (delta) => {
    const a = new Date(anchorDate + "T00:00:00");
    if (period === PERIOD_WEEK) {
      a.setDate(a.getDate() + delta * 7);
    } else if (period === PERIOD_MONTH) {
      a.setMonth(a.getMonth() + delta);
    } else {
      a.setDate(a.getDate() + delta);
    }
    setAnchorDate(isoString(a));
  };
  const goToday = () => {
    if (period === PERIOD_WEEK) setAnchorDate(isoString(isoMonday()));
    else setAnchorDate(isoString(new Date()));
  };
  const goPicker = () => setActiveSiteId(null);

  const periodLabel = useMemo(() => {
    const a = new Date(anchorDate + "T00:00:00");
    const fmt = (d) =>
      `${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
    if (period === PERIOD_WEEK && scheduleData?.dates?.length) {
      const s = new Date(scheduleData.dates[0] + "T00:00:00");
      const e = new Date(scheduleData.dates[scheduleData.dates.length - 1] + "T00:00:00");
      return `${s.getFullYear()}. ${fmt(s)} – ${fmt(e)}`;
    }
    if (period === PERIOD_MONTH) {
      const months = [
        "Január", "Február", "Március", "Április", "Május", "Június",
        "Július", "Augusztus", "Szeptember", "Október", "November", "December",
      ];
      return `${a.getFullYear()}. ${months[a.getMonth()]}`;
    }
    // DAY
    return `${a.getFullYear()}. ${fmt(a)}`;
  }, [period, anchorDate, scheduleData]);

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <div className="space-y-6" data-testid="schedule-page">
      <PageHeader
        periodLabel={periodLabel}
        loading={loading}
        onPrev={() => shiftPeriod(-1)}
        onNext={() => shiftPeriod(1)}
        onToday={goToday}
        showBack={activeSiteId !== null}
        onBack={goPicker}
        activeName={
          activeSiteId === VIEW_OVERVIEW
            ? "Összes telephely"
            : activeSection?.site_name
        }
        period={period}
        onPeriodChange={setPeriod}
        showActions={
          activeSiteId !== null && activeSiteId !== VIEW_OVERVIEW
        }
        onManageTemplates={() => setTemplatesManagerOpen(true)}
        templatesCount={templates.length}
        onOpenDrafts={() => setDraftsManagerOpen(true)}
        draftsPendingCount={draftsPendingCount}
        showDraftsButton={activeSiteId !== null}
        onPublish={onPublish}
        publishStatus={publishStatus}
      />

      {activeSiteId === null ? (
        <SitePickerGrid
          sections={sections}
          loading={loading}
          onPick={(id) => setActiveSiteId(id)}
        />
      ) : activeSiteId === VIEW_OVERVIEW ? (
        <OverviewSections
          sections={sections}
          dates={scheduleData?.dates || []}
        />
      ) : !activeSection ? (
        <EmptyState />
      ) : period === PERIOD_WEEK ? (
        <ScheduleGrid
          section={activeSection}
          dates={scheduleData?.dates || []}
          templates={templates}
          onAssignTemplate={onAssignTemplate}
          onShiftClick={openShiftEditor}
          onApproveDraft={onApproveDraft}
          onRejectDraft={onRejectDraft}
          approvingDraftId={approvingDraftId}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
        />
      ) : period === PERIOD_MONTH ? (
        <MonthGrid
          section={activeSection}
          monthStart={fromDate}
          monthEnd={toDate}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onShiftClick={openShiftEditor}
          onApproveDraft={onApproveDraft}
          onRejectDraft={onRejectDraft}
          approvingDraftId={approvingDraftId}
        />
      ) : (
        <DayList
          section={activeSection}
          date={anchorDate}
          templates={templates}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onShiftClick={openShiftEditor}
          onAssignTemplate={onAssignTemplate}
          onApproveDraft={onApproveDraft}
          onRejectDraft={onRejectDraft}
          approvingDraftId={approvingDraftId}
        />
      )}

      {/* Bulk-select floating toolbar */}
      {selectedIds.size > 0 && (
        <SelectionToolbar
          count={selectedIds.size}
          onClear={() => setSelectedIds(new Set())}
          onDelete={() => setBulkDeleteOpen(true)}
          onPublish={
            activeSiteId !== null && activeSiteId !== VIEW_OVERVIEW
              ? onPublish
              : null
          }
        />
      )}

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`${selectedIds.size} műszak törlése`}
        description="Ez a művelet nem visszavonható. A kijelölt műszakok véglegesen eltávolításra kerülnek a beosztásból."
        confirmLabel="Igen, törlés"
        cancelLabel="Mégse"
        onConfirm={onBulkDelete}
        loading={bulkDeleting}
        variant="danger"
        testId="bulk-delete-confirm"
      />

      <ConfirmDialog
        open={publishConfirm.open}
        onOpenChange={(o) => setPublishConfirm((p) => ({ ...p, open: o }))}
        title={`${publishConfirm.ids.length} műszak publikálása`}
        description="Az érintett dolgozók email értesítést kapnak a beosztásukról. Publikálás után a műszakok megjelennek a dolgozók saját nézetében."
        confirmLabel="Igen, publikálás"
        cancelLabel="Mégse"
        onConfirm={doPublish}
        loading={publishing}
        variant="default"
        testId="publish-confirm"
      />

      <TemplatesManagerDialog
        open={templatesManagerOpen}
        onOpenChange={setTemplatesManagerOpen}
        siteId={activeSiteId !== VIEW_OVERVIEW ? activeSiteId : null}
        siteName={activeSection?.site_name}
      />
      <DraftsManagerDialog
        open={draftsManagerOpen}
        onOpenChange={setDraftsManagerOpen}
        siteId={activeSiteId !== VIEW_OVERVIEW ? activeSiteId : null}
        from={fromDate}
        to={toDate}
        onChanged={() => {
          loadSchedule();
          loadDraftsCount();
        }}
      />
      <ShiftEditDialog
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        shift={shiftEditing}
        onSaved={loadSchedule}
      />
    </div>
  );
}

// =====================================================================
// PageHeader (cím, hét navigátor, vissza gomb)
// =====================================================================
function PageHeader({
  periodLabel,
  loading,
  onPrev,
  onNext,
  onToday,
  showBack,
  onBack,
  activeName,
  period,
  onPeriodChange,
  showActions,
  onManageTemplates,
  templatesCount = 0,
  onOpenDrafts,
  draftsPendingCount = 0,
  showDraftsButton = false,
  onPublish,
  publishStatus,
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          {showBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-foreground"
              data-testid="back-to-sites"
            >
              <ArrowLeft className="h-3 w-3" />
              <span className="hidden sm:inline">Vissza a telephelyekhez</span>
              <span className="sm:hidden">Vissza</span>
            </button>
          ) : (
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <CalendarRange className="h-3 w-3" /> Műszakszervezés
            </p>
          )}
          <h1 className="truncate text-xl font-semibold tracking-tight sm:text-2xl">
            {showBack ? activeName || "Beosztás" : "Beosztás"}
          </h1>
          <p className="mt-1 hidden max-w-xl text-sm text-muted-foreground sm:block">
            {showBack
              ? "Kattints a + ikonra egy cellában a műszak hozzáadásához. Sablonokat a fenti „Sablonok kezelése\u201D gombbal hozhatsz létre."
              : "Válassz telephelyet a heti beosztás kezeléséhez. A sablonok és műszakok telephelyenként külön kerülnek tárolásra."}
          </p>
        </div>

        {/* Navigator + actions group */}
        <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
          {/* Period navigator */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card px-1 py-0.5 shadow-[0_8px_22px_-18px_rgba(15,23,42,0.3)]">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-1.5"
              onClick={onPrev}
              data-testid="week-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <button
              type="button"
              onClick={onToday}
              className="px-2 text-[12px] font-semibold tabular-nums tracking-tight transition-colors hover:text-indigo-600"
              data-testid="week-label"
            >
              {periodLabel || "—"}
            </button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-1.5"
              onClick={onNext}
              data-testid="week-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            {loading && (
              <Loader2 className="ml-1 h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>

          {showActions && (
            <>
              <PeriodSwitcher value={period} onChange={onPeriodChange} />
            </>
          )}

          {showDraftsButton && (
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "relative h-9 shrink-0 gap-2 pl-3 pr-3 text-[12px] font-medium",
                draftsPendingCount > 0
                  && "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-200",
              )}
              onClick={onOpenDrafts}
              data-testid="open-drafts-btn"
            >
              <FileEdit className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">Tervezetek</span>
              <span className="sm:hidden">Tervezet</span>
              {draftsPendingCount > 0 && (
                <span
                  className="ml-0.5 inline-grid h-4 min-w-[16px] place-items-center rounded-full bg-amber-200 px-1 font-mono text-[10px] font-semibold tabular-nums text-amber-900 dark:bg-amber-800/70 dark:text-amber-100"
                  data-testid="drafts-pending-badge"
                >
                  {draftsPendingCount}
                </span>
              )}
            </Button>
          )}

          {showActions && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-9 shrink-0 gap-2 pl-3 pr-3 text-[12px] font-medium"
                onClick={onManageTemplates}
                data-testid="manage-templates-btn"
              >
                <Layers className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span className="hidden sm:inline">Sablonok kezelése</span>
                <span className="sm:hidden">Sablonok</span>
                {templatesCount > 0 && (
                  <span className="ml-0.5 inline-grid h-4 min-w-[16px] place-items-center rounded-full bg-slate-100 px-1 font-mono text-[10px] font-semibold tabular-nums text-slate-700 dark:bg-foreground/[0.08] dark:text-foreground">
                    {templatesCount}
                  </span>
                )}
              </Button>
              <Button
                size="sm"
                className={cn(
                  "h-9 shrink-0 gap-1.5 text-[12px] font-semibold transition-all",
                  publishStatus?.allPublished
                    ? "bg-emerald-100 text-emerald-700 shadow-none hover:bg-emerald-200/80 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25"
                    : "bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-[0_6px_18px_-8px_rgba(16,185,129,0.5)] hover:from-emerald-700 hover:to-emerald-800",
                )}
                onClick={onPublish}
                disabled={publishStatus?.allPublished || publishStatus?.total === 0}
                title={
                  publishStatus?.allPublished
                    ? "Minden műszak publikálva"
                    : publishStatus?.total === 0
                      ? "Nincs publikálható műszak"
                      : `${publishStatus?.unpublished || 0} publikálandó`
                }
                data-testid="publish-btn"
              >
                <Send className="h-3.5 w-3.5" strokeWidth={2} />
                {publishStatus?.allPublished ? "Publikálva" : "Publikálás"}
                {!publishStatus?.allPublished && publishStatus?.unpublished > 0 && (
                  <span className="ml-0.5 inline-grid h-4 min-w-[16px] place-items-center rounded-full bg-white/25 px-1 font-mono text-[10px] font-semibold tabular-nums">
                    {publishStatus.unpublished}
                  </span>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PeriodSwitcher({ value, onChange }) {
  const items = [
    { key: "week", label: "Heti", desktopOnly: true },
    { key: "month", label: "Havi", desktopOnly: true },
    { key: "day", label: "Napi" },
  ];
  return (
    <div
      className="inline-flex items-center rounded-lg border border-border bg-card p-0.5 shadow-[0_4px_12px_-8px_rgba(15,23,42,0.2)]"
      role="tablist"
    >
      {items.map((it) => {
        const active = value === it.key;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className={cn(
              "rounded-md px-3 py-1 text-[11.5px] font-semibold tracking-tight transition-all",
              it.desktopOnly && "hidden sm:inline-flex",
              active
                ? "bg-foreground text-background shadow-[0_2px_6px_-2px_rgba(15,23,42,0.4)]"
                : "text-slate-600 hover:text-foreground dark:text-muted-foreground",
            )}
            data-testid={`period-${it.key}`}
            role="tab"
            aria-selected={active}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================
// PendingDraftsInlineBanner — szembetűnő sáv a rács fölött, ha vannak
// függőben lévő tervezetek az aktuális tartományra
// =====================================================================
function PendingDraftsInlineBanner({ count, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="pending-drafts-banner"
      className="flex w-full items-center gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50 px-4 py-3 text-left shadow-[0_4px_14px_-8px_rgba(245,158,11,0.4)] transition-all hover:shadow-[0_6px_18px_-8px_rgba(245,158,11,0.5)] dark:border-amber-900/40 dark:from-amber-950/30 dark:to-amber-950/10"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-500 text-white shadow-[0_4px_10px_-3px_rgba(245,158,11,0.6)]">
        <FileEdit className="h-4 w-4" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-amber-900 dark:text-amber-100">
          {count} függőben lévő dolgozói tervezet
        </p>
        <p className="mt-0.5 text-[12px] text-amber-700 dark:text-amber-300">
          Kattints ide a megtekintéshez és jóváhagyáshoz.
        </p>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={2} />
    </button>
  );
}


// =====================================================================
// SelectionToolbar — floating bottom bar when shifts are selected
// =====================================================================
function SelectionToolbar({ count, onClear, onDelete, onPublish }) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center"
      data-testid="selection-toolbar"
    >
      <div className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-border bg-card/95 px-3 py-2 shadow-[0_18px_60px_-20px_rgba(15,23,42,0.4)] backdrop-blur-md">
        <span className="rounded-full bg-foreground/[0.06] px-3 py-1 font-mono text-[11px] font-semibold tabular-nums">
          {count} kijelölve
        </span>
        {onPublish && (
          <Button
            size="sm"
            onClick={onPublish}
            className="h-8 gap-1.5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-[12px] text-white shadow-[0_4px_12px_-4px_rgba(16,185,129,0.5)] hover:from-emerald-700 hover:to-emerald-800"
            data-testid="bulk-publish"
          >
            <Send className="h-3.5 w-3.5" />
            Publikálás
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onDelete}
          className="h-8 gap-1.5 border-rose-200 text-[12px] text-rose-700 hover:border-rose-300 hover:bg-rose-50"
          data-testid="bulk-delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Törlés
        </Button>
        <button
          type="button"
          onClick={onClear}
          className="grid h-8 w-8 place-items-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          aria-label="Kijelölés megszüntetése"
          data-testid="bulk-clear"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// SitePickerGrid — telephely választó kártyák (mint Pozíciók)
// =====================================================================
function SitePickerGrid({ sections, loading, onPick }) {
  if (loading && !sections.length) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-36 animate-pulse rounded-xl border border-border bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (!sections.length) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
          <Building2 className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <p className="text-base font-medium">Még nincs telephely</p>
        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          Hozz létre telephelyet a Cégek modulban, hogy beoszthasd a dolgozóidat.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="site-picker-grid"
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
    >
      {/* Összesítő kártya — első kártya */}
      <OverviewCard sections={sections} onClick={() => onPick(VIEW_OVERVIEW)} />
      {sections.map((s) => (
        <SiteCard key={s.site_id} section={s} onClick={() => onPick(s.site_id)} />
      ))}
    </div>
  );
}

function SiteCard({ section, onClick }) {
  const empCount = section.employees?.length || 0;
  const totalHours = (section.employees || []).reduce(
    (s, e) => s + (Number(e.weekly_hours) || 0),
    0,
  );
  const scheduled = (section.employees || []).filter(
    (e) => (e.shifts || []).length > 0,
  ).length;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`site-card-${section.site_id}`}
      className="group flex flex-col rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-5 text-left transition-colors hover:border-foreground/20"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold tracking-tight">
              {section.site_name}
            </h3>
          </div>
          {section.company_name && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {section.company_name}
            </p>
          )}
        </div>
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-foreground to-foreground/80 text-background">
          <Building2 className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
        <StatChip icon={Users} label="Dolgozó" value={empCount} />
        <StatChip icon={Clock} label="Óra" value={fmtHours(totalHours)} />
        <StatChip
          icon={CalendarRange}
          label="Beosztva"
          value={`${scheduled}/${empCount}`}
        />
      </div>

      <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground transition-colors group-hover:text-indigo-600">
        Beosztás megnyitása
        <ChevronRight className="h-3 w-3" />
      </span>
    </button>
  );
}

function OverviewCard({ sections, onClick }) {
  const totalEmployees = sections.reduce(
    (s, sec) => s + (sec.employees?.length || 0),
    0,
  );
  const totalHours = sections.reduce(
    (s, sec) =>
      s + (sec.employees || []).reduce((a, e) => a + (Number(e.weekly_hours) || 0), 0),
    0,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="site-card-overview"
      className="group relative flex flex-col overflow-hidden rounded-xl border border-foreground/20 bg-gradient-to-br from-foreground to-foreground/90 p-5 text-left text-background transition-all hover:border-foreground/40 hover:shadow-[0_18px_40px_-20px_rgba(15,23,42,0.55)]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:18px_18px]"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold tracking-tight">
              Összes telephely
            </h3>
          </div>
          <p className="mt-0.5 truncate text-xs text-background/70">
            Áttekintő nézet — minden telephely egy oldalon
          </p>
        </div>
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-background/30 bg-background/10 text-background">
          <Eye className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>

      <div className="relative mt-4 flex flex-wrap items-center gap-2 text-[11px]">
        <StatChip icon={MapPin} label="Telephely" value={sections.length} dark />
        <StatChip icon={Users} label="Dolgozó" value={totalEmployees} dark />
        <StatChip icon={Clock} label="Óra" value={fmtHours(totalHours)} dark />
      </div>

      <span className="relative mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-background/80 transition-colors group-hover:text-background">
        Áttekintés megnyitása
        <ChevronRight className="h-3 w-3" />
      </span>
    </button>
  );
}

function StatChip({ icon: Icon, label, value, dark }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium tabular-nums",
        dark
          ? "border-background/20 bg-background/10 text-background/90"
          : "border-border bg-background text-foreground",
      )}
    >
      <Icon className="h-3 w-3 opacity-70" strokeWidth={1.75} />
      <span className="opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

// =====================================================================
// OverviewSections (összes telephely collapse-olt listája)
// =====================================================================
function OverviewSections({ sections, dates }) {
  const [open, setOpen] = useState({});

  useEffect(() => {
    if (Object.keys(open).length || !sections.length) return;
    const init = {};
    sections.slice(0, 3).forEach((s) => (init[s.site_id] = true));
    setOpen(init);
  }, [sections]);

  if (sections.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4" data-testid="overview-sections">
      {sections.map((section) => {
        const isOpen = !!open[section.site_id];
        const totalHours = (section.employees || []).reduce(
          (s, e) => s + (Number(e.weekly_hours) || 0),
          0,
        );
        return (
          <div
            key={section.site_id}
            className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_18px_40px_-30px_rgba(15,23,42,0.18)]"
          >
            <button
              type="button"
              onClick={() =>
                setOpen((p) => ({ ...p, [section.site_id]: !p[section.site_id] }))
              }
              className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-foreground/[0.02]"
              data-testid={`overview-toggle-${section.site_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-foreground to-foreground/80 text-background">
                  <Building2 className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-sm font-semibold tracking-tight">
                    {section.site_name}
                  </p>
                  <p className="text-[11.5px] text-muted-foreground">
                    {section.employees?.length || 0} dolgozó · összesen {fmtHours(totalHours)} óra
                  </p>
                </div>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {isOpen && (
              <div className="border-t border-border bg-background/40 p-3">
                <ScheduleGrid
                  section={section}
                  dates={dates}
                  onDropTemplate={() => {
                    toast.info(
                      "Sablon-húzáshoz nyisd meg a telephely beosztását külön.",
                    );
                  }}
                  onShiftClick={() => {
                    toast.info(
                      "Szerkesztéshez nyisd meg a telephely beosztását külön.",
                    );
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-muted/20 px-6 py-16 text-center">
      <CalendarRange className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
      <p className="mt-3 text-sm font-medium text-muted-foreground">
        Nincs megjeleníthető adat erre a hétre.
      </p>
    </div>
  );
}
